import { z } from 'zod/v4';
import {
  BaseStepOptions,
  CalculationType,
  calculationTypeSchema,
  createDefinitionSchema,
  createThresholdOptionsSchema,
  stepOptionsSchema,
  unitOptionsSchema,
} from '../../utils';
import { newOverrideObject } from '../../utils/overrides';

export enum TransformKind {
  Aggregation = 'Aggregation',
  SeriesToColumns = 'SeriesToColumns',
  Raw = 'Raw',
}

const numberFormatThresholdStep = stepOptionsSchema.extend({
  background_color: z.string().optional(),
});
const numberFormatThresholdOptionsSchema = createThresholdOptionsSchema(numberFormatThresholdStep).extend({
  base: numberFormatThresholdStep.omit({ value: true }).optional(),
});

export interface NumberFormatThresholdStep extends z.TypeOf<typeof numberFormatThresholdStep> {}
export interface NumberFormatThresholdOptionsSchema extends z.TypeOf<typeof numberFormatThresholdOptionsSchema> {}

export enum TableFormatOverrideKind {
  Number = 'Number',
  String = 'String',
  Time = 'Time',
}

const numberFormatOverrides = z.object({
  kind: z.literal(TableFormatOverrideKind.Number),
  thresholds: numberFormatThresholdOptionsSchema.optional(),
  unit: unitOptionsSchema.optional(),
});

const stringFormatOverride = newOverrideObject(
  z.object({
    color: z.string().optional(),
    background_color: z.string().optional(),
    name: z.string().optional(),
  })
);

const stringFormatOverrides = z.object({
  kind: z.literal(TableFormatOverrideKind.String),
  overrides: z.array(stringFormatOverride).optional(),
});

const timeFormatOverrides = z.object({
  kind: z.literal(TableFormatOverrideKind.Time),
  // Future options can be added for more control over time formatting
});

const tableOverridesSchema = newOverrideObject(
  z.object({
    displayName: z.string().optional(),
    hidden: z.boolean().optional(),
    // N.B.: Some customer schemas have null stored for width, so we need to accept it as valid.
    // We use z.preprocess to convert null to undefined before validation.
    width: z.preprocess((val) => (val === null ? undefined : val), z.number().optional()),
    format: z.union([numberFormatOverrides, stringFormatOverrides, timeFormatOverrides]).optional(),
  })
);

export interface TableOverridesSchema extends z.TypeOf<typeof tableOverridesSchema> {}

export interface TableOverridesObject extends z.TypeOf<typeof tableOverridesSchema> {}
export type TableOverridesProperties = TableOverridesObject['properties'];
export type TableOverrides = TableOverridesProperties;

export type FormatOverrides = NonNullable<TableOverridesProperties['format']>;
export interface NumberFormatOverrides extends z.TypeOf<typeof numberFormatOverrides> {}
export interface StringFormatOverrides extends z.TypeOf<typeof stringFormatOverrides> {}
export interface StringFormatOverride extends z.TypeOf<typeof stringFormatOverride> {}

export type AggregationTransform = {
  kind: TransformKind.Aggregation;
  labelsToColumns?: boolean;
  stats?: CalculationType[];
};
const aggregationTransformSchema = z.object({
  kind: z.literal(TransformKind.Aggregation),
  labelsToColumns: z.boolean().optional(),
  stats: z.array(calculationTypeSchema).optional(),
});

export type SeriesToColumnsTransform = {
  kind: TransformKind.SeriesToColumns;
};
const seriesToColumnsTransformSchema = z.object({
  kind: z.literal(TransformKind.SeriesToColumns),
});

export type RawTransform = {
  kind: TransformKind.Raw;
  labelsToColumns?: boolean;
};

const rawTransformSchema = z.object({
  kind: z.literal(TransformKind.Raw),
  labelsToColumns: z.boolean().optional(),
});

export type TableSortOption = z.infer<typeof tableSortOptionSchema>;
const tableSortOptionSchema = z.enum(['asc', 'desc', 'default']);

export interface TableSort extends z.TypeOf<typeof tableSortSchema> {}
const tableSortSchema = z.object({
  sortBy: z.string().optional(),
  sortOrder: tableSortOptionSchema.optional(),
});

export type TableTransform = AggregationTransform | SeriesToColumnsTransform | RawTransform;
const tableTransformSchema = z.union([aggregationTransformSchema, seriesToColumnsTransformSchema, rawTransformSchema]);

export interface TableThresholdStepOption extends z.TypeOf<typeof numberFormatThresholdStep> {}
export interface TableThresholdBaseOption extends BaseStepOptions<TableThresholdStepOption> {}
export interface TableThresholdOptions extends z.TypeOf<typeof numberFormatThresholdOptionsSchema> {}

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the bar chart.
 */
export const tableSpecSchema = z.object({
  transform: tableTransformSchema.optional(),
  unit: unitOptionsSchema.optional(),
  height: z.number().optional(),
  sort: z.array(tableSortSchema).optional(),
  columnOverrides: z.array(tableOverridesSchema).optional(),
  column_order: z.array(z.string()).optional(),
  thresholds: numberFormatThresholdOptionsSchema.optional(),
});

/**
 * Type for the spec-driven `DashboardPanel` variant of the bar chart.
 */
export interface TableSpec extends z.TypeOf<typeof tableSpecSchema> {}

export const tableChartDefinitionSchema = createDefinitionSchema('Table', tableSpecSchema);

/**
 * The schema for a BarChart `DashboardPanel` definition.
 */
export interface TableChartDefinition extends z.TypeOf<typeof tableChartDefinitionSchema> {}

/**
 * Version of the Zod schema to be used for base types for the props-driven `Viz` variant of the table chart.
 */
export const tableVizSchema = z.object({
  // TODO: think more about how we can decompose the spec without breaking the
  // spec modification functionality in `TablePanel`.
  spec: tableSpecSchema,
});

/**
 * Base type for the props-driven `Viz` variant of the bar chart generated from the Zod schema and
 * converting all snake_case properties to camelCase. This interface should be extended in `viz-framework`
 * to do the following:
 * - Add additional types for `Viz` only use cases.
 * - Extend properties to add more meaningful jsdoc. This is necessary because both `z.Typeof` and
 *   `CamelCasedPropertiesDeep` lose jsdoc.
 */
export interface TablePanelPropsBase extends z.TypeOf<typeof tableVizSchema> {}
